import { Button } from '@skipper/ui';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import {
    ImageSelectModal,
    FileListItem,
    ImageModalState,
    FileEntryStatus,
} from '@components/modals/imageSelectModal/ImageSelectModal';
import {
    Activity,
    Assignment,
    FileEntry,
    SingleSubmission,
    StudentSubmission,
    SubmitAssignmentSubmissionMutation,
    useStartAssignmentSubmissionMutation,
    useSubmitAssignmentSubmissionMutation,
    useUpdateAssignmentSubmissionMutation,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useDispatchError } from '@hooks/error/useDispatchError';
import { useInvalidateQueries } from '@hooks/useInvalidateQueries';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { UseMutationOptions } from 'react-query/types/react/types';
import { FileListAttachment } from '@components/activities/AssignmentSubmissionsView';
import { SubmissionSuccessSubmitSVG } from '@components/common/svgs/SubmissionSuccessSubmitSVG';
import { XIcon } from '@heroicons/react/outline';

type AssignmentStartSubmitVariables = {
    attachmentIds: string[];
    content?: string;
    fileListItems: FileListItem[];
};
type AssignmentItem = Omit<Activity, 'studentSubmissions'> & { item: Assignment } & {
    studentSubmissions: (StudentSubmission & SingleSubmission)[];
};
export function useAssignmentSaveDraft<TError = unknown, TContext = unknown>(
    {
        activity,
        submissionId,
        studentEnrollmentId,
    }: { activity: AssignmentItem; submissionId?: string; studentEnrollmentId?: string },
    options?: UseMutationOptions<
        SubmitAssignmentSubmissionMutation,
        TError,
        AssignmentStartSubmitVariables,
        TContext
    >,
) {
    const startSubmission = useStartAssignmentSubmissionMutation(getClient('submissions'), {
        onSettled: (data) => {},
    });
    const updateSubmission = useUpdateAssignmentSubmissionMutation(getClient('submissions'), {});
    return useMutation(
        async ({
            attachmentIds,
            content,
        }: {
            attachmentIds: string[];
            content?: string;
            fileListItems: FileListItem[];
        }) => {
            let existingSubmissionId = submissionId;
            if (!existingSubmissionId) {
                const startSubmissionResult = await startSubmission.mutateAsync({
                    activityId: activity?.id,
                    attachmentIds: attachmentIds,
                    studentEnrollmentId: studentEnrollmentId,
                });

                if (startSubmissionResult?.startAssignmentSubmission?.errors?.[0]) {
                    throw new Error(startSubmissionResult.startAssignmentSubmission.errors[0]);
                }
                existingSubmissionId =
                    startSubmissionResult?.startAssignmentSubmission?.submission?.id;
            }
            const submittedResult = await updateSubmission.mutateAsync({
                addAttachmentIds: attachmentIds,
                submissionId: existingSubmissionId,
                // content: 'Test field',
            });
            return submittedResult;
        },
        options,
    );
}

export function useAssignmentSubmit<TError = unknown, TContext = unknown>(
    { activity, submissionId }: { activity: AssignmentItem; submissionId?: string },
    options?: UseMutationOptions<
        SubmitAssignmentSubmissionMutation,
        TError,
        AssignmentStartSubmitVariables,
        TContext
    >,
) {
    const startSubmission = useStartAssignmentSubmissionMutation(getClient('submissions'), {
        onSettled: (data) => {},
    });
    const submit = useSubmitAssignmentSubmissionMutation(getClient('submissions'), {});
    return useMutation(
        async ({
            attachmentIds,
            content,
        }: {
            attachmentIds: string[];
            content?: string;
            fileListItems: FileListItem[];
        }) => {
            let existingSubmissionId = submissionId;
            if (!existingSubmissionId) {
                const startSubmissionResult = await startSubmission.mutateAsync({
                    activityId: activity?.id,
                    attachmentIds: attachmentIds,
                });
                if (startSubmissionResult?.startAssignmentSubmission?.errors?.[0]) {
                    throw new Error(startSubmissionResult.startAssignmentSubmission.errors[0]);
                }
                existingSubmissionId =
                    startSubmissionResult?.startAssignmentSubmission?.submission?.id;
            }
            const submittedResult = await submit.mutateAsync({
                addAttachmentIds: attachmentIds,
                submissionId: existingSubmissionId,
            });
            return submittedResult;
        },
        options,
    );
}

export function useUploaderModalSubmission({
    activity,
    enrollmentId,
    isGradebook = false,
}: {
    activity: AssignmentItem;
    enrollmentId?: string;
    isGradebook?: boolean;
}) {
    const { toggle } = useUniversalModalDispatch();
    const invalidateQueries = useInvalidateQueries();
    const dispatch = useDispatchError();
    const activityName = activity?.item?.title || '';

    // the idea is that stidents don't pass the enrollmentId when uploading files because they don't need it

    const submissionId = !!enrollmentId
        ? activity?.studentSubmissions?.find((el) => el?.enrollment?.id === enrollmentId)
              ?.submission?.id
        : activity?.studentSubmissions?.[0]?.submission?.id;

    const submission = useAssignmentSaveDraft(
        { activity, submissionId, studentEnrollmentId: enrollmentId },
        {
            onSettled: async (data: any, error: Error, vars) => {
                if (data?.updateAssignmentSubmission?.success) {
                    await invalidateQueries(
                        'CourseUnitActivitiesData',
                        'CourseUnitsData',
                        'CoursesWithActivitiesAndSubmissions',
                        'CoursesWithActivitiesAndSubmissionsData',
                        'CoursesActivitySubmissionsByEnrollments',
                    );
                    if (!enrollmentId)
                        toggle(
                            true,
                            <IntermediateSubmissionsView
                                files={vars?.fileListItems || []}
                                submissionId={data?.updateAssignmentSubmission?.submission?.id}
                                activityName={activityName}
                                enrollmentId={enrollmentId}
                                isGradebook={isGradebook}
                            />,
                        );
                } else if (error) {
                    dispatch(error?.message || 'Something went wrong');
                } else {
                    dispatch(
                        data?.submitAssignmentSubmission?.errors?.[0] || 'Something went wrong',
                    );
                }
            },
        },
    );

    const handleSubmissionModal = useCallback(
        (startingModalTab?: ImageModalState, fileList?: any[], onSuccess?: any) => {
            if (fileList?.length > 0) {
                const attachmentIds = fileList.map((file) => file.id);
                submission.mutate({
                    attachmentIds,
                    fileListItems: fileList,
                });
            } else {
                toggle(
                    true,
                    <ImageSelectModal
                        allowedFileTypes="any"
                        isMultiFile
                        isImmediateUpdate
                        startingOpenTab={startingModalTab}
                        updater={(fileId) => {}}
                        onFinished={(fileList) => {
                            const attachmentIds = fileList?.map(({ file }) => file?.id);
                            submission.mutate({
                                attachmentIds,
                                fileListItems: fileList,
                            });
                            toggle(false);
                            if (onSuccess) onSuccess();
                        }}
                    />,
                );
            }
        },
        [toggle, submission, submissionId],
    );

    return useMemo(() => ({ handleSubmissionModal }), [handleSubmissionModal]);
}

export function IntermediateSubmissionsView({
    files,
    submissionId,
    activityName,
    enrollmentId,
    isGradebook,
}: {
    files: FileListItem[];
    submissionId?: string;
    activityName: string;
    enrollmentId?: string;
    isGradebook?: boolean;
}) {
    const [selectedFiles, setSelectedFiles] = useState(files);
    const invalidateQueries = useInvalidateQueries();
    const { toggle } = useUniversalModalDispatch();
    const dispatch = useDispatchError();
    const updateSubmission = useUpdateAssignmentSubmissionMutation(getClient('submissions'), {
        onSettled: (data, _errors, vars) => {
            if (data?.updateAssignmentSubmission?.success) {
                invalidateQueries('CoursesActivitySubmissionsByEnrollments');
                setSelectedFiles((fi) => {
                    const newFiles = fi?.filter((f) => !vars?.removeAttachmentIds?.includes(f?.id));
                    if (newFiles?.length === 0) {
                        toggle(false);
                    }
                    return newFiles;
                });
            } else {
                dispatch(data?.updateAssignmentSubmission?.errors?.[0] || 'Something went wrong');
            }
        },
    });

    const submit = useSubmitAssignmentSubmissionMutation(getClient('submissions'), {
        onSettled: (data) => {
            if (data?.submitAssignmentSubmission?.success) {
                if (isGradebook) {
                    invalidateQueries('gradebookActivitiesData');
                } else {
                    invalidateQueries('CoursesActivitySubmissionsByEnrollments');
                }
                toggle(true, <SuccessfulSubmissionModal activityName={activityName} />);
            } else {
                dispatch(data?.submitAssignmentSubmission?.errors?.[0] || 'Something went wrong');
            }
        },
    });

    return (
        <div className="fixed inset-0 z-9999">
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative flex flex-col bg-white rounded-md overflow-hidden max-w-[640px] w-full min-h-[300px] p-6 text-passport">
                    <div>
                        <div className="flex justify-between items-start ">
                            <p className="font-inter text-lg font-[500] mb-4">
                                Assignment Submission
                            </p>
                            <Button variant="textOnly" onClick={() => toggle(false, null)}>
                                <XIcon className="w-6 h-6 text-gray-400" />
                            </Button>
                        </div>
                        <p className="font-inter text-md font-[400] mb-[10px]">
                            You are turning in the following files
                        </p>
                    </div>
                    <div className="min-h-fit flex-grow flex-1 flex flex-col w-full gap-y-2 mb-[34px]">
                        {selectedFiles?.map((file) => (
                            <FileListAttachment
                                key={file?.title}
                                item={file}
                                showDelete={true}
                                onDelete={() =>
                                    updateSubmission.mutate({
                                        submissionId,
                                        removeAttachmentIds: [file?.id],
                                    })
                                }
                            />
                        ))}
                    </div>
                    <div className="border-t border-gray-200 pt-[21px] w-full flex justify-between">
                        <div>
                            <Button onClick={() => toggle(false)} size="medium" variant="white">
                                Cancel
                            </Button>
                        </div>
                        <div className="flex gap-x-3">
                            <Button
                                onClick={() => {
                                    submit.mutate({
                                        submissionId,
                                        addAttachmentIds: selectedFiles?.map(
                                            (file) => file?.file?.id,
                                        ),
                                    });
                                }}
                                size="medium"
                                variant="secondary">
                                Turn in Submission
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function SuccessfulSubmissionModal({
    activityName,
    onClose,
    timeout,
}: {
    activityName: string;
    onClose?: () => void;
    timeout?: number;
}) {
    const { toggle } = useUniversalModalDispatch();
    const timer = timeout || 5000;

    useEffect(() => {
        let timeout = setTimeout(() => {
            toggle(false);
            onClose?.();
        }, timer);
        return () => clearTimeout(timeout);
    }, [toggle]);

    return (
        <div className="fixed inset-0 z-9999">
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative object-contain bg-white rounded-md overflow-hidden max-w-[480px] max-h-[480px] w-full  h-full p-6 text-passport">
                    <div className="flex w-full items-center justify-center pt-[52px] mb-[8px]">
                        <SubmissionSuccessSubmitSVG />
                    </div>
                    <div>
                        <p className="font-[700] text-[40px] font-larsseit text-center mb-4">
                            Thanks for submitting!
                        </p>
                        <p className="text-[24px] font-larsseit font-[700] text-center text-[#01AAB9]">
                            {activityName || ''}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// function FileListAttachment({ item, onDelete }: { item: FileListItem; onDelete: () => void }) {
//     const selectIcon = useFileTypeImage();
//     const { file } = item;
//     return (
//         <div
//             className={`flex justify-between items-center p-2 rounded-md z-[3] ${getBackgroundColorForFile(
//                 file.title,
//             )}`}>
//             <div className="flex items-center mr-3 h-full gap-x-2 truncate">
//                 <div className="min-w-[39px]">
//                     <NextImage src={selectIcon(file?.fileType)} />
//                 </div>
//                 <div className="truncate">
//                     <p className="truncate">{file.title}</p>
//                     <p>{`${convertToBytes(file.originalFileSize)}\u200b`}</p>
//                 </div>
//             </div>
//             <div className="flex gap-x-2 items-center">
//                 {/* <Button
//                     as="a"
//                     // onClick={(e) => {
//                     //     e?.preventDefault();
//                     // }}
//                     size="small"
//                     shape="fixedSquare"
//                     variant="white"
//                     // href={tryMakeDownloadUrl(fileUrl ?? '')}
//                     type={file.originalFileContentType}
//                     download={file.title}>
//                     <DownloadIcon className="w-5 h-5 stroke-gray-500" />
//                 </Button> */}

//                 <Button onClick={onDelete} size="small" shape="fixedSquare" variant="white">
//                     <TrashIcon className="w-5 h-5 stroke-gray-500" />
//                 </Button>
//             </div>
//         </div>
//     );
// }
